import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const VideoContainer = styled(motion.div)`
  position: relative;
  width: 100%;
  max-width: ${props => props.maxWidth || '900px'};
  margin: 2rem auto;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
`;

const StyledVideo = styled.video`
  width: 100%;
  display: block;
  object-fit: cover;
  border-radius: 12px;
`;

const Controls = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
  padding: 1rem;
  display: flex;
  align-items: center;
  opacity: ${props => props.isVisible ? 1 : 0};
  transition: opacity 0.3s;
`;

const PlayPauseButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:focus {
    outline: none;
  }
`;

const ProgressContainer = styled.div`
  flex: 1;
  margin: 0 1rem;
  height: 6px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
  position: relative;
  cursor: pointer;
`;

const Progress = styled.div`
  height: 100%;
  background: ${props => props.progressColor || '#0066cc'};
  border-radius: 3px;
  width: ${props => props.width};
`;

const TimeDisplay = styled.div`
  color: white;
  font-size: 0.9rem;
  margin-left: 1rem;
  font-family: monospace;
`;

const MuteButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:focus {
    outline: none;
  }
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.4);
  opacity: ${props => props.isVisible ? 1 : 0};
  transition: opacity 0.3s;
  pointer-events: ${props => props.isVisible ? 'all' : 'none'};
`;

const PlayButton = styled.button`
  background: ${props => props.buttonColor || '#0066cc'};
  border: none;
  color: white;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  transition: transform 0.2s, background 0.2s;
  
  &:hover {
    transform: scale(1.1);
    background: ${props => props.buttonHoverColor || '#0055aa'};
  }
  
  &:focus {
    outline: none;
  }
`;

const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
};

// Helper function to extract YouTube video ID from URL
const getYouTubeId = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

const VideoPlayer = ({ 
  src,
  poster,
  maxWidth = '900px',
  progressColor = '#0066cc',
  buttonColor = '#0066cc',
  buttonHoverColor = '#0055aa',
  animationDelay = 0,
  title = ''
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [showOverlay, setShowOverlay] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  
  const videoRef = useRef(null);
  const progressRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  
  // Check if source is a YouTube URL
  const youtubeId = getYouTubeId(src);
  
  useEffect(() => {
    const video = videoRef.current;
    
    // Only set up video event listeners if we have a regular video element (not YouTube)
    if (!youtubeId && video) {
      const handleTimeUpdate = () => {
        setCurrentTime(video.currentTime);
        setProgress((video.currentTime / video.duration) * 100);
      };
      
      const handleLoadedMetadata = () => {
        setDuration(video.duration);
      };
      
      const handleEnded = () => {
        setIsPlaying(false);
        setShowOverlay(true);
      };
      
      video.addEventListener('timeupdate', handleTimeUpdate);
      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      video.addEventListener('ended', handleEnded);
      
      return () => {
        video.removeEventListener('timeupdate', handleTimeUpdate);
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        video.removeEventListener('ended', handleEnded);
      };
    }
  }, [youtubeId]);
  
  // Separate useEffect for mobile detection
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsMobile(window.innerWidth <= 768);
      
      const handleResize = () => {
        setIsMobile(window.innerWidth <= 768);
      };
      
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);
  
  const handlePlayPause = () => {
    const video = videoRef.current;
    
    // Only handle play/pause for regular videos, not YouTube
    if (!youtubeId && video) {
      if (isPlaying) {
        video.pause();
      } else {
        video.play();
      }
      setIsPlaying(!isPlaying);
      setShowOverlay(false);
    }
  };
  
  const handleProgressClick = (e) => {
    const video = videoRef.current;
    
    // Only handle progress click for regular videos, not YouTube
    if (!youtubeId && video && progressRef.current) {
      const rect = progressRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;
      const newTime = (clickX / width) * video.duration;
      
      video.currentTime = newTime;
      setCurrentTime(newTime);
      setProgress((newTime / video.duration) * 100);
    }
  };
  
  const handleMuteToggle = () => {
    const video = videoRef.current;
    
    // Only handle mute for regular videos, not YouTube
    if (!youtubeId && video) {
      video.muted = !video.muted;
      setIsMuted(video.muted);
    }
  };
  
  const handleMouseMove = () => {
    setShowControls(true);
    
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  };
  
  const handleMouseLeave = () => {
    if (isPlaying) {
      setShowControls(false);
    }
  };
  
  // Render YouTube iframe if youtubeId is available, otherwise render the regular video player
  if (youtubeId) {
    return (
      <VideoContainer
        maxWidth={maxWidth}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: animationDelay }}
      >
        <div style={{ position: 'relative', paddingTop: '56.25%', width: '100%' }}>
          <iframe 
            style={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              width: '100%',
              height: '100%', 
              border: 'none',
              borderRadius: '12px'
            }}
            src={`https://www.youtube.com/embed/${youtubeId}?rel=0&showinfo=0&autoplay=0`}
            title={title || 'YouTube Video'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </VideoContainer>
    );
  }
  
  // Regular video player
  return (
    <VideoContainer
      maxWidth={maxWidth}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: animationDelay }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <StyledVideo
        ref={videoRef}
        src={src}
        poster={poster}
        onClick={handlePlayPause}
        preload="metadata"
      />
      
      <Overlay isVisible={showOverlay}>
        <PlayButton 
          onClick={handlePlayPause}
          buttonColor={buttonColor}
          buttonHoverColor={buttonHoverColor}
          aria-label="Play video"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="currentColor">
            <polygon points="5 3 19 12 5 21 5 3" />
          </svg>
        </PlayButton>
      </Overlay>
      
      <Controls isVisible={showControls || !isPlaying}>
        <PlayPauseButton onClick={handlePlayPause} aria-label={isPlaying ? "Pause" : "Play"}>
          {isPlaying ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="4" width="4" height="16" />
              <rect x="14" y="4" width="4" height="16" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
          )}
        </PlayPauseButton>
        
        <ProgressContainer 
          ref={progressRef}
          onClick={handleProgressClick}
        >
          <Progress 
            width={`${progress}%`}
            progressColor={progressColor}
          />
        </ProgressContainer>
        
        <TimeDisplay>
          {formatTime(currentTime)} / {formatTime(duration)}
        </TimeDisplay>
        
        <MuteButton onClick={handleMuteToggle} aria-label={isMuted ? "Unmute" : "Mute"}>
          {isMuted ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <line x1="23" y1="9" x2="17" y2="15" />
              <line x1="17" y1="9" x2="23" y2="15" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
            </svg>
          )}
        </MuteButton>
      </Controls>
    </VideoContainer>
  );
};

export default VideoPlayer;
